import {useTranslation} from 'react-i18next'
import {SectionCard} from '@/components/section-card'
import {PorQue} from '@/components/ui/por-que'
import {SegmentoResumenTag} from '@/components/bateria/nivel-tag'
import {type AgregadoCuestionario} from '@/hooks/bateria/use-bateria'
import {enRiesgoPct, prettifyTaxonomia} from '@/lib/bateria'

type Grupo = {
  key: string
  labelKey: string
  data: Record<string, AgregadoCuestionario>
  nPor: Record<string, number>
}

function segmentoLabel(key: string): string {
  return key.startsWith('(') ? key : prettifyTaxonomia(key)
}

/** Read-only, worst-first; drilling down is the segmentador's job. */
function GrupoResumen({grupo}: {grupo: Grupo}) {
  const {t} = useTranslation()
  const rows = Object.entries(grupo.data)
    .map(([key, agregado]) => ({key, agregado, n: grupo.nPor[key] ?? 0}))
    .sort((a, b) => enRiesgoPct(b.agregado.total_general) - enRiesgoPct(a.agregado.total_general))

  if (rows.length === 0) return null

  return (
    <div data-testid={`caracterizacion-grupo-${grupo.key}`}>
      <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
        {t(grupo.labelKey)}
      </p>
      <ul className="mt-1.5 divide-y divide-border">
        {rows.map(({key, agregado, n}) => (
          <li key={key} className="flex flex-wrap items-center justify-between gap-2 py-1.5">
            <span className="truncate text-sm text-foreground">
              {segmentoLabel(key)} <span className="font-mono text-[11px] text-muted-foreground">· {n}</span>
            </span>
            <SegmentoResumenTag dist={agregado.total_general} />
          </li>
        ))}
      </ul>
    </div>
  )
}

export function ResultadosCaracterizacion({
  porArea,
  porCargo,
  porSede,
  porTipoVinculacion,
  nPorArea,
  nPorCargo,
  nPorSede,
  nPorTipoVinculacion,
}: {
  porArea: Record<string, AgregadoCuestionario>
  porCargo: Record<string, AgregadoCuestionario>
  porSede: Record<string, AgregadoCuestionario>
  porTipoVinculacion: Record<string, AgregadoCuestionario>
  nPorArea: Record<string, number>
  nPorCargo: Record<string, number>
  nPorSede: Record<string, number>
  nPorTipoVinculacion: Record<string, number>
}) {
  const {t} = useTranslation()
  const grupos: Grupo[] = [
    {
      key: 'area',
      labelKey: 'admin.bateria.resultados.grupoArea',
      data: porArea,
      nPor: nPorArea,
    },
    {
      key: 'cargo',
      labelKey: 'admin.bateria.resultados.grupoCargo',
      data: porCargo,
      nPor: nPorCargo,
    },
    {
      key: 'sede',
      labelKey: 'admin.bateria.resultados.grupoSede',
      data: porSede,
      nPor: nPorSede,
    },
    {
      key: 'vinculacion',
      labelKey: 'admin.bateria.resultados.grupoVinculacion',
      data: porTipoVinculacion,
      nPor: nPorTipoVinculacion,
    },
  ]
  const conDatos = grupos.filter((g) => Object.keys(g.data).length > 0)
  if (conDatos.length === 0) return null

  return (
    <SectionCard
      testId="resultados-caracterizacion"
      title={t('admin.bateria.resultados.caracterizacionTitle')}
      bodyClassName="px-5 py-4">
      <div className="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2">
        {conDatos.map((g) => (
          <GrupoResumen key={g.key} grupo={g} />
        ))}
      </div>
      {/* T7: la procedencia se conserva entera, pero bajo demanda. */}
      <div className="surface-divider mt-4 border-t pt-3">
        <PorQue
          label={t('admin.bateria.resultados.caracterizacionDeDondeSalio')}
          testId="caracterizacion-nota-demografica"
          contenidoTestId="caracterizacion-nota-demografica-contenido">
          {t('admin.bateria.resultados.caracterizacionNotaDemografica')}
        </PorQue>
      </div>
    </SectionCard>
  )
}
